import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { ToastKind } from '../../components/Toast';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../../components/ui/Dialog';
import { Textarea } from '../../components/ui/Input';
import { cn } from '../../components/ui/cn';
import { useLanguage } from '../../lib/i18n';
import { FEATURE_REQUEST_MAX_LENGTH, FEATURE_REQUEST_MIN_LENGTH, featureRequestLength } from './support';

interface FeatureRequestDialogProps {
  open: boolean;
  onClose: () => void;
  /** "Bunun yerine bir sorun mu yaşıyorsun? Destekle iletişime geç" */
  onContactSupport: () => void;
  onNotify: (kind: ToastKind, text: string) => void;
}

/**
 * Özellik isteği penceresi. Metin düz metin olarak gönderilir; ana süreç
 * normalleştirip denetler (kod/HTML, görünmez karakterler, bağlantı sayısı),
 * veritabanı da uzunluk ve sıklık sınırını ayrıca uygular. Ana sürecin
 * reddetme sebebi pencerede gösterilir.
 */
export const FeatureRequestDialog: React.FC<FeatureRequestDialogProps> = ({
  open,
  onClose,
  onContactSupport,
  onNotify,
}) => {
  const { t } = useLanguage();
  // Taslak pencere kapanınca silinmiyor — yanlışlıkla kapatılırsa kaybolmasın.
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    // Pencere çizildikten sonra odaklan.
    const id = window.setTimeout(() => textareaRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  const length = featureRequestLength(text);
  const isTooShort = length < FEATURE_REQUEST_MIN_LENGTH;
  const isTooLong = length > FEATURE_REQUEST_MAX_LENGTH;
  const canSend = !isSending && !isTooShort && !isTooLong;

  const submit = async () => {
    if (!canSend) return;
    setIsSending(true);
    setError(null);
    try {
      await window.zabit.submitFeatureRequest(text);
      setText('');
      onClose();
      onNotify('success', t('Teşekkürler! İsteğin ekibe ulaştı.', 'Thanks! Your request reached the team.'));
    } catch (err) {
      setError((err as Error).message || t('İstek gönderilemedi.', 'Could not send the request.'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isSending) onClose();
      }}
      className="max-w-md"
      title={t('Özellik iste', 'Request a feature')}
      description={t('TalkNote’u senin için ne daha iyi yapardı?', 'What would make TalkNote better for you?')}
      footer={
        <div className="flex w-full flex-col gap-4">
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={isSending}>
              {t('Vazgeç', 'Cancel')}
            </Button>
            <Button onClick={() => void submit()} disabled={!canSend}>
              {isSending && <Loader2 className="size-4 animate-spin" />}
              {t('Gönder', 'Submit')}
            </Button>
          </div>
          <p className="text-center text-[0.8rem] text-muted-foreground">
            {t('Bunun yerine bir sorun mu yaşıyorsun?', 'Having an issue instead?')}{' '}
            <button
              type="button"
              onClick={onContactSupport}
              className="font-medium text-foreground underline-offset-4 hover:underline"
            >
              {t('Destekle iletişime geç', 'Contact support')}
            </button>
          </p>
        </div>
      }
    >
      <div className="space-y-2">
        <Textarea
          ref={textareaRef}
          value={text}
          rows={6}
          maxLength={FEATURE_REQUEST_MAX_LENGTH + 200}
          spellCheck
          placeholder={t('İstediğin özelliği anlat…', 'Describe the feature you’d like…')}
          aria-label={t('Özellik isteği', 'Feature request')}
          aria-invalid={error ? true : undefined}
          onChange={(event) => {
            setText(event.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(event) => {
            // Ctrl/Cmd + Enter gönderir; düz Enter yeni satır.
            if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
              event.preventDefault();
              void submit();
            }
          }}
        />
        <div className="flex items-start justify-between gap-4 text-[0.8rem]">
          <span className={cn(error ? 'font-medium text-destructive' : 'text-muted-foreground')}>
            {error ??
              (length > 0 && isTooShort
                ? t(
                    `En az ${FEATURE_REQUEST_MIN_LENGTH} karakter yaz.`,
                    `Write at least ${FEATURE_REQUEST_MIN_LENGTH} characters.`
                  )
                : t('Düz metin — kod ya da HTML gönderilmez.', 'Plain text only — no code or HTML.'))}
          </span>
          <span className={cn('shrink-0 tabular-nums', isTooLong ? 'text-destructive' : 'text-muted-foreground')}>
            {length} / {FEATURE_REQUEST_MAX_LENGTH}
          </span>
        </div>
      </div>
    </Dialog>
  );
};
